/**
 * Formatting utilities for numbers, percentages, and currency values
 * Provides consistent thousand separators and decimal formatting across the app
 */

/**
 * Formats a number with thousand separators
 * @param {number|string|null} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 0)
 * @returns {string} - Formatted number with thousand separators
 */
export const formatWithThousands = (value, decimals = 0) => {
  if (value == null || value === "" || value === "–") return "–";
  
  const num = parseFloat(value);
  if (isNaN(num)) return "–";
  
  // Format with specified decimal places and add thousand separators
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formats a percentage value with thousand separators for large percentages
 * @param {number|string|null} value - The value to format (as decimal, e.g., 0.1 for 10%)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted percentage with thousand separators if needed
 */
export const formatPercentWithThousands = (value, decimals = 2) => {
  if (value == null || value === "" || value === "–") return "–";
  
  const num = parseFloat(value);
  if (isNaN(num)) return "–";
  
  // Convert to percentage
  const percentage = num * 100;
  
  // Format with thousand separators if needed
  const formatted = percentage.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  
  return `${formatted} %`;
};

/**
 * Formats a dollar amount with thousand separators
 * @param {number|string|null} value - The value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted currency with thousand separators
 */
export const formatCurrencyWithThousands = (value, decimals = 2) => {
  if (value == null || value === "" || value === "–") return "–";
  
  const num = parseFloat(value);
  if (isNaN(num)) return "–";
  
  // Format as currency with thousand separators
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formats a regular number with thousand separators and smart decimal places
 * For numbers >= 10, uses 1 decimal place, otherwise uses 2
 * @param {number|string|null} value - The value to format
 * @returns {string} - Formatted number with thousand separators
 */
export const formatNumberSmart = (value) => {
  if (value == null || value === "" || value === "–") return "–";
  
  const num = parseFloat(value);
  if (isNaN(num)) return "–";
  
  const decimals = Math.abs(num) >= 10 ? 1 : 2;
  
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formats an integer with thousand separators (no decimals)
 * @param {number|string|null} value - The value to format
 * @returns {string} - Formatted integer with thousand separators
 */
export const formatIntegerWithThousands = (value) => {
  if (value == null || value === "" || value === "–") return "–";
  
  const num = parseInt(value);
  if (isNaN(num)) return "–";
  
  return num.toLocaleString('en-US');
};